// Formatted SOA: statement templates, the field catalog and the API shapes (backend utils/soaTemplate.ts,
// services/formattedSoa*.ts; docs/FORMATTED_SOA.md). The backend normalises every template it stores, so these
// types mirror utils/soaTemplate.ts; change both together.
//
// Units: positions, sizes, margins and padding in millimetres; font sizes, border and line widths in points.

export type SoaPageSize = 'A4' | 'Letter' | 'Legal' | 'A5';
export type SoaOrientation = 'portrait' | 'landscape';
export type SoaAlign = 'left' | 'center' | 'right' | 'justify';
export type SoaVAlign = 'top' | 'middle' | 'bottom';
export type SoaBorderStyle = 'solid' | 'dashed' | 'dotted' | 'double' | 'none';
export type SoaBandKey = 'pageHeader' | 'reportHeader' | 'reportFooter' | 'pageFooter';
export type SoaCardType = 'C' | 'S';
export type SoaAppliesTo = 'ALL' | 'C' | 'S';
/** As Customer SOA: the open items' age from the invoice (posting) date or the due date, at the To date. */
export type SoaAgingBasis = 'invoice' | 'due';

export interface SoaBorderSide {
  /** pt; 0 = no line */
  width: number;
  style: SoaBorderStyle;
  color: string;
}

export interface SoaBorders {
  top?: SoaBorderSide;
  right?: SoaBorderSide;
  bottom?: SoaBorderSide;
  left?: SoaBorderSide;
}

/** Every key is optional: an element inherits the template defaults for what it leaves out. */
export interface SoaStyle {
  fontFamily?: string;
  /** pt */
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  uppercase?: boolean;
  /** '#rrggbb' */
  color?: string;
  /** '#rrggbb', or '' for none */
  background?: string;
  align?: SoaAlign;
  valign?: SoaVAlign;
  /** multiple of the font size */
  lineHeight?: number;
  /** pt */
  letterSpacing?: number;
  /** mm, every side */
  padding?: number;
  direction?: 'ltr' | 'rtl' | 'auto';
  borders?: SoaBorders;
}

export type SoaElementType = 'text' | 'field' | 'image' | 'line' | 'box' | 'kv' | 'html';

export interface SoaElementBase {
  id: string;
  type: SoaElementType;
  /** Designer label only. */
  name?: string;
  /** mm from the band's top-left corner */
  x: number;
  y: number;
  w: number;
  h: number;
  style: SoaStyle;
  /** Text, field, kv and html: the box grows with its content and pushes the elements below it down. */
  canGrow?: boolean;
  /** Not printed when its value is empty. */
  hideIfEmpty?: boolean;
}

/** Static text; may hold placeholders such as {{bp.name}} or {{totals.closing|amount}}. */
export interface SoaTextElement extends SoaElementBase {
  type: 'text';
  text: string;
}

/** One field of the catalog, formatted. */
export interface SoaFieldElement extends SoaElementBase {
  type: 'field';
  field: string;
  /** e.g. 'date', 'date:DD MMM, YYYY', 'amount', 'amount:2', 'drcr', 'upper' (FORMAT_OPTIONS) */
  format?: string;
  prefix?: string;
  suffix?: string;
}

export interface SoaImageElement extends SoaElementBase {
  type: 'image';
  /** company.logo = the logo in Company Management > Report settings */
  source: 'company.logo' | 'custom';
  /** data:image/... for source 'custom' */
  dataUrl?: string;
  fit?: 'contain' | 'cover' | 'fill';
}

export interface SoaLineElement extends SoaElementBase {
  type: 'line';
  orientation: 'horizontal' | 'vertical';
  /** pt */
  thickness: number;
  color: string;
  lineStyle: SoaBorderStyle;
}

/** A rectangle: style.background and style.borders. */
export interface SoaBoxElement extends SoaElementBase {
  type: 'box';
  /** mm */
  radius?: number;
}

export interface SoaKvRow {
  label: string;
  /** text with placeholders */
  value: string;
}

/** A label / value table, e.g. ACCOUNT CODE | {{bp.code}}. */
export interface SoaKvElement extends SoaElementBase {
  type: 'kv';
  rows: SoaKvRow[];
  /** percent of the element's width */
  labelWidth: number;
  labelStyle: SoaStyle;
  valueStyle: SoaStyle;
  gridColor: string;
  /** pt; 0 = no grid */
  gridWidth: number;
}

/** Raw HTML (placeholders allowed). */
export interface SoaHtmlElement extends SoaElementBase {
  type: 'html';
  html: string;
}

export type SoaElement =
  | SoaTextElement
  | SoaFieldElement
  | SoaImageElement
  | SoaLineElement
  | SoaBoxElement
  | SoaKvElement
  | SoaHtmlElement;

export interface SoaBand {
  visible: boolean;
  /** mm; the printed band grows when its content needs more */
  height: number;
  background?: string;
  elements: SoaElement[];
}

export interface SoaColumn {
  id: string;
  /** a 'row' field of the catalog */
  field: string;
  header: string;
  /** mm; the columns are scaled to the table's width */
  width: number;
  align: SoaAlign;
  format?: string;
  /** Text wraps onto more lines; otherwise one line, shrunk to fit. */
  wrap: boolean;
  style?: SoaStyle;
  headerStyle?: SoaStyle;
}

export interface SoaSummaryRow {
  show: boolean;
  label: string;
  /** id of the column that prints the label */
  labelColumn: string;
  style: SoaStyle;
}

export interface SoaTable {
  columns: SoaColumn[];
  headerStyle: SoaStyle;
  cellStyle: SoaStyle;
  /** background of every other transaction row; '' = none */
  zebra: string;
  gridColor: string;
  /** pt; 0 = no grid */
  gridWidth: number;
  verticalLines: boolean;
  horizontalLines: boolean;
  outerBorder: boolean;
  /** The column headings repeat on every page. */
  repeatHeader: boolean;
  /** First row: the balance brought forward (balance columns show the opening balance). */
  openingRow: SoaSummaryRow;
  /** Last row: the period's debit and credit totals and the closing balance. */
  closingRow: SoaSummaryRow & { showTotals: boolean };
  /** Printed when the period has no transactions. */
  emptyText: string;
  /** mm */
  spaceBefore: number;
  /** mm */
  spaceAfter: number;
}

export interface SoaFormats {
  /** e.g. 'DD/MM/YYYY' (tokens DD D MM M MMM MMMM YY YYYY HH mm ss) */
  date: string;
  dateTime: string;
  /** null = the company's (Company Management > amount decimal places, else by currency) */
  amountDecimals: number | null;
  thousands: boolean;
  negative: 'minus' | 'parentheses';
  zero: 'show' | 'blank' | 'dash';
}

export interface SoaTemplateDefinition {
  version: 1;
  page: {
    size: SoaPageSize;
    orientation: SoaOrientation;
    /** mm */
    margin: { top: number; right: number; bottom: number; left: number };
  };
  defaults: { fontFamily: string; fontSize: number; color: string; lineHeight: number };
  bands: Record<SoaBandKey, SoaBand>;
  table: SoaTable;
  formats: SoaFormats;
  /** SAP transaction type (13, 24, ...) -> short code printed by the 'docType' field (SIV, RV, ...) */
  docTypes: Record<string, string>;
  /** Advanced: CSS added after the generated styles; JavaScript run before the pages are laid out. */
  custom: { css: string; js: string };
}

export type SoaFieldGroup = 'company' | 'bp' | 'statement' | 'totals' | 'page' | 'row';
export type SoaFieldType = 'text' | 'date' | 'datetime' | 'amount' | 'number' | 'image';

export interface SoaFieldDef {
  /** placeholder path, e.g. 'bp.name'; row fields are column keys, e.g. 'debit' */
  key: string;
  label: string;
  group: SoaFieldGroup;
  type: SoaFieldType;
  sample?: string | number;
}

export interface SoaTemplateSummary {
  templateId: number;
  companyId: number;
  name: string;
  description: string;
  appliesTo: SoaAppliesTo;
  isDefault: boolean;
  updatedDate: string;
  updatedByName: string | null;
}

export interface SoaTemplateRecord extends SoaTemplateSummary {
  definition: SoaTemplateDefinition;
}

export interface SoaTemplateVersion {
  historyId: number;
  savedDate: string;
  savedByName: string | null;
}

export interface SoaPermissions {
  /** Statement Templates (designer) */
  design: boolean;
  /** customer statements (all customers, or the assigned ones) */
  customers: boolean;
  /** every customer of the company; otherwise only the assigned ones */
  allCustomers: boolean;
  suppliers: boolean;
}

export interface SoaConfig {
  companyId: number;
  companyName: string;
  permissions: SoaPermissions;
  templates: SoaTemplateSummary[];
  fields: SoaFieldDef[];
  fonts: string[];
  formats: Array<{ value: string; label: string; types: SoaFieldType[] }>;
  starters: { standard: SoaTemplateDefinition; blank: SoaTemplateDefinition };
  defaults: { fromDate: string; toDate: string };
}

export interface SoaPartner {
  cardCode: string;
  cardName: string;
  foreignName?: string | null;
  balance?: number | null;
}

export type SoaRunStatus = 'Queued' | 'Running' | 'Done' | 'Failed' | 'Cancelled';

export interface SoaRun {
  runId: number;
  companyId: number;
  templateName: string;
  cardType: SoaCardType;
  fromDate: string;
  toDate: string;
  basis: SoaAgingBasis;
  total: number;
  done: number;
  status: SoaRunStatus;
  message: string | null;
  errors: Array<{ cardCode: string; message: string }>;
  hasPdf: boolean;
  hasZip: boolean;
  pages: number | null;
  createdByName: string | null;
  createdDate: string;
  finishedDate: string | null;
}

/** Body of POST /formatted-soa/preview and /pdf: a saved template, or the designer's unsaved definition. */
export interface SoaRenderRequest {
  companyId: number;
  templateId?: number;
  definition?: SoaTemplateDefinition;
  cardType: SoaCardType;
  /** preview: one BP (or none with sample: true); pdf: 1 to 25 */
  cardCodes: string[];
  fromDate: string;
  toDate: string;
  /** ageing basis (default invoice) */
  basis?: SoaAgingBasis;
  /** preview only: made-up data, no SAP read */
  sample?: boolean;
}

/** Company details printed on statements: SAP's AdminInfo, or the value kept in Company Management. */
export interface SoaCompanyDetails {
  name: string;
  foreignName: string;
  address: string;
  foreignAddress: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  taxId: string;
  logo: string | null;
  currency: string;
}

export const SOA_BAND_LABELS: Record<SoaBandKey, string> = {
  pageHeader: 'Page header (every page)',
  reportHeader: 'Report header (first page)',
  reportFooter: 'Report footer (after the table)',
  pageFooter: 'Page footer (every page)',
};

export const SOA_BAND_ORDER: SoaBandKey[] = ['pageHeader', 'reportHeader', 'reportFooter', 'pageFooter'];

export const SOA_PAGE_SIZES_MM: Record<SoaPageSize, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
  Legal: { w: 215.9, h: 355.6 },
  A5: { w: 148, h: 210 },
};

/** Page size in mm after the orientation. */
export function soaPageSizeMm(def: SoaTemplateDefinition): { w: number; h: number } {
  const s = SOA_PAGE_SIZES_MM[def.page.size] ?? SOA_PAGE_SIZES_MM.A4;
  return def.page.orientation === 'landscape' ? { w: s.h, h: s.w } : { w: s.w, h: s.h };
}

/** Printable width between the left and right margins, in mm. */
export function soaContentWidthMm(def: SoaTemplateDefinition): number {
  return soaPageSizeMm(def).w - def.page.margin.left - def.page.margin.right;
}
